/**
 * TotalsRow.jsx - a totals row that follows the table's columns (10/9/26).
 * The Projects "Won totals" (projects/WonTotalsRow.jsx) and the
 * Opportunities totals both render through it, so a hidden column's total
 * is hidden with it and every total sits under its own column in whatever
 * order the person put them.
 *
 *   cols   the ids of the cells on screen, in order (the Actions cell included)
 *   cells  { colId: { content, className?, bg?, accent? } } - the columns with a total
 *   fills  { colId: bg } - a tint for an EMPTY cell (the invoice group's)
 *   label  the words, put in the first run of columns without a total
 *
 * Columns without a total merge into one cell per run (same tint), so the
 * colSpans always add up to the table's columns.
 *
 * Passing `stickyTop` (px below the top of the scroll pane) turns on the
 * pinned copy: a bold dark-green BAR that reads as part of the header block
 * (Sharif: "under the black headers, not in the table"). Every cell then
 * needs its OWN opaque background: a sticky <td> floats over the scrolling
 * rows while the <tr> background stays behind them.
 */
import { useEffect, useRef, useState } from 'react';

const TOTALS_Z = 4; // under the header cells (z 5)
const BAR_BG = '#124116'; // pinned bar: darker than gat-green
const BAR_TEXT = { color: '#fff', fontWeight: 800, fontSize: '12.5px' };

export default function TotalsRow({ cols, cells, fills = {}, label, stickyTop, rowRef, pad = 'px-3 py-2' }) {
  const pinned = stickyTop != null;
  // Non-pinned copy: the light table-row styling, `undefined` where there is
  // no tint. Pinned: opaque dark bar + white bold text (inline styles outrank
  // the light per-group Tailwind classes on each cell).
  const style = (bg, accent) =>
    pinned
      ? {
          position: 'sticky',
          top: stickyTop,
          zIndex: TOTALS_Z,
          background: BAR_BG,
          borderBottom: '2px solid #0B2D0E',
          ...(accent || BAR_TEXT),
        }
      : bg
        ? { background: bg }
        : undefined;

  // runs of columns without a total, split where the tint changes
  const parts = [];
  for (const col of cols) {
    const c = cells[col];
    const last = parts[parts.length - 1];
    if (c) parts.push({ col, cell: c });
    else if (last && !last.cell && last.bg === fills[col]) last.span += 1;
    else parts.push({ col, span: 1, bg: fills[col] });
  }
  const labelAt = parts.findIndex((p) => !p.cell);

  return (
    <tr ref={rowRef} data-totals="" className={pinned ? 'font-bold' : 'bg-surface-3 border-t-2 border-gat-green font-bold'}>
      {parts.map((p, i) =>
        p.cell ? (
          <td
            key={p.col}
            className={`${pad} text-right tabular-nums whitespace-nowrap ${p.cell.className ?? 'text-gat-greenDark'}`}
            style={style(p.cell.bg, p.cell.accent)}
          >
            {p.cell.content}
          </td>
        ) : (
          <td
            key={p.col}
            colSpan={p.span > 1 ? p.span : undefined}
            className={i === labelAt ? `${pad} text-text-dark text-[12.5px]` : undefined}
            style={style(p.bg)}
          >
            {i === labelAt &&
              (pinned ? (
                <span style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                  {label.title} <span style={{ opacity: 0.75, fontWeight: 700 }}>· {label.count} records</span>
                </span>
              ) : (
                <>
                  {label.title} · {label.count} records
                </>
              ))}
          </td>
        )
      )}
    </tr>
  );
}

// First-paint offset for the sticky column headers (they sit under the pinned
// totals bar), corrected from the DOM on mount by usePinnedHeight.
// 33px = 8px py-2 top/bottom + ~17px of 12.5px bold text.
export const TOTALS_H_FALLBACK = 33;

/**
 * [ref, height] of the pinned totals bar. The headers stick just below it,
 * and its height comes out of font metrics rather than a number we control,
 * so it is read off the DOM; 0 when there is no bar (headers at the top).
 * `dep` re-measures (row count, columns: a scrollbar or a wrap can reflow it).
 */
export function usePinnedHeight(dep) {
  const ref = useRef(null);
  const [h, setH] = useState(TOTALS_H_FALLBACK);
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      if (h !== 0) setH(0);
      return;
    }
    const next = Math.round(el.getBoundingClientRect().height);
    if (next > 0 && next !== h) setH(next);
  }, [h, dep]);
  return [ref, h];
}
